import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import css from './ListingImageViewer.module.css';

/** Open the largest uncropped image in a keyboard-accessible, distraction-free viewer. */
const ListingImageViewer = ({ image, alt, children }) => {
  const intl = useIntl();
  const trigger = useRef(null);
  const dialog = useRef(null);
  const pointer = useRef(null);
  const [open, setOpen] = useState(false);
  const variants = Object.entries(image?.attributes?.variants || {})
    .filter(([name, variant]) => name.startsWith('scaled-') && variant?.url)
    .map(([, variant]) => variant)
    .sort((a, b) => b.width * b.height - a.width * a.height);
  const url = variants[0]?.url;

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const activeDialog = dialog.current;
    activeDialog.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      activeDialog.close();
      document.body.style.overflow = previousOverflow;
      trigger.current?.focus({ preventScroll: true });
    };
  }, [open]);

  if (!url) return children;

  const handleClick = event => {
    // Keep native new-tab gestures and support browsers without modal dialogs.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (pointer.current?.dragged) {
      event.preventDefault();
      pointer.current = null;
      return;
    }
    if (typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
    event.preventDefault();
    setOpen(true);
  };

  return (
    <>
      <a
        ref={trigger}
        className={css.trigger}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={intl.formatMessage({ id: 'ListingImageViewer.enlarge' }, { image: alt })}
        aria-haspopup="dialog"
        onClick={handleClick}
        onPointerDown={event => {
          pointer.current = { x: event.clientX, y: event.clientY, dragged: false };
        }}
        onPointerMove={event => {
          if (pointer.current) {
            pointer.current.dragged =
              pointer.current.dragged ||
              Math.abs(event.clientX - pointer.current.x) > 10 ||
              Math.abs(event.clientY - pointer.current.y) > 10;
          }
        }}
        onKeyDown={() => {
          pointer.current = null;
        }}
        onDragStart={event => event.preventDefault()}
      >
        {children}
      </a>
      {open &&
        createPortal(
          <dialog
            ref={dialog}
            className={css.dialog}
            aria-label={alt}
            onCancel={event => {
              event.preventDefault();
              setOpen(false);
            }}
            onClose={() => setOpen(false)}
            onClick={event => {
              if (event.target === event.currentTarget) setOpen(false);
            }}
          >
            <div className={css.toolbar}>
              <a className={css.control} href={url} target="_blank" rel="noopener noreferrer">
                <FormattedMessage id="ListingImageViewer.openOriginal" />
              </a>
              <button
                type="button"
                className={css.control}
                onClick={() => setOpen(false)}
                autoFocus
              >
                <FormattedMessage id="ListingImageViewer.close" />
              </button>
            </div>
            <img className={css.image} src={url} alt={alt} />
          </dialog>,
          document.body
        )}
    </>
  );
};

export default ListingImageViewer;
